import { useNavigate } from '@tanstack/react-router';

import { SearchAffordance } from '#/components/layout/CategoryNavigation/shared';
import { buildSearchState } from '#/components/search/searchPagination';
import { BrandAccent } from '#/components/ui/BrandAccent';
import { Icon } from '#/components/ui/icons';
import { HOMEPAGE_FALLBACK } from '#/content/homepage';

interface HomepageHeroProps {
  title?: string;
  description?: string;
  trustSignals?: string[];
}

export function HomepageHero({
  title = HOMEPAGE_FALLBACK.heroTitle,
  description = HOMEPAGE_FALLBACK.heroDescription,
  trustSignals = HOMEPAGE_FALLBACK.trustSignals,
}: HomepageHeroProps) {
  const navigate = useNavigate();
  const handleSubmit = (trimmedQuery: string) => {
    void navigate({
      to: '/search',
      search: buildSearchState({
        query: trimmedQuery,
        investment_type: undefined,
      }),
    });
  };

  return (
    <div className="flex flex-col gap-32 pt-24 pb-48 lg:gap-48 lg:pt-32 lg:pb-72">
      <div className="grid min-w-0 gap-32 lg:grid-cols-[minmax(0,1fr)_16rem] lg:items-center lg:gap-48">
        <div className="flow flow-20 min-w-0">
          <BrandAccent variant="long" />
          <div className="flow flow-20">
            <h1 className="text-fg-header text-3xl font-semibold tracking-[-0.03em] lg:text-5xl">
              {title}
            </h1>
            <p className="text-fg-subheader mb-32 leading-loose">
              {description}
            </p>
          </div>

          <SearchAffordance
            searchLabel="Search market data"
            searchPrompt="Find ETFs, stocks, and mutual funds"
            searchPlaceholder="ETFs, stocks, mutual funds"
            enablePreview
            trackContext="homepage_search"
            formClassName="min-w-0 overflow-hidden lg:pl-24"
            onSubmitQuery={handleSubmit}
          />
        </div>

        <ul className="grid gap-12 lg:gap-16 lg:justify-self-end">
          {trustSignals.map((signal) => (
            <li key={signal} className="flex items-center gap-16">
              <span className="text-green-70 bg-green-30 flex size-32 shrink-0 items-center justify-center rounded-full">
                <Icon id="check" size={18} />
              </span>
              <span className="text-fg-subheader text-static-18 leading-normal font-medium">
                {signal}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
